/**
 * Breakpoint values matching Tailwind CSS defaults.
 * These are the pixel thresholds where each breakpoint begins.
 */

export const BREAKPOINTS = {
  xs: 0, // Base: < 640px (no Tailwind prefix needed)
  sm: 640,
  md: 768, // Mobile threshold
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/**
 * Media query strings for use with window.matchMedia().
 *
 * Unlike Tailwind's min-width only approach (e.g., md: applies at 768px+),
 * the named breakpoint queries (xs, sm, md, lg, xl) use bounded ranges for
 * exact breakpoint detection. The semantic aliases (mobile, tablet, desktop)
 * use unbounded ranges for broader category detection.
 */
export const MEDIA_QUERIES = {
  /** Extra small: < 640px (smaller phones) */
  xs: `(max-width: ${BREAKPOINTS.sm - 1}px)`,
  /** Small: 640-767px (larger phones) */
  sm: `(min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md - 1}px)`,
  /** Medium: 768-1023px (tablets) */
  md: `(min-width: ${BREAKPOINTS.md}px) and (max-width: ${BREAKPOINTS.lg - 1}px)`,
  /** Large: 1024-1279px (small desktops) */
  lg: `(min-width: ${BREAKPOINTS.lg}px) and (max-width: ${BREAKPOINTS.xl - 1}px)`,
  /** Extra large: 1280-1535px (large desktops) */
  xl: `(min-width: ${BREAKPOINTS.xl}px) and (max-width: ${BREAKPOINTS['2xl'] - 1}px)`,
  /** 2x Extra large: >= 1536px (wide screens) */
  '2xl': `(min-width: ${BREAKPOINTS['2xl']}px)`,

  // Semantic aliases for convenience
  /** Mobile: < 768px (combines xs + sm) */
  mobile: `(max-width: ${BREAKPOINTS.md - 1}px)`,
  /** Tablet: 768-1023px (alias for md) */
  tablet: `(min-width: ${BREAKPOINTS.md}px) and (max-width: ${BREAKPOINTS.lg - 1}px)`,
  /** Desktop: >= 1024px */
  desktop: `(min-width: ${BREAKPOINTS.lg}px)`,
} as const;
